import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import appSettings from '../../../config/settings';
import { formatMoney } from '../../../util/currency';
import { types as sdkTypes } from '../../../util/sdkLoader';

import {
  Modal,
  Button,
  FieldCurrencyInput,
  FieldTextInput,
  Form,
} from '../../../components';

import css from './BidModal.module.css';

const { Money } = sdkTypes;

const BidModal = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    onSubmitBid,
    sendBidInProgress,
    sendBidError,
    marketplaceCurrency,
    listingTitle,
    minBidAmountInSubunits,
  } = props;

  const intl = useIntl();

  const priceRequired = validators.required(
    intl.formatMessage({ id: 'BidModal.bidAmountRequired' })
  );
  const minBidAmount = typeof minBidAmountInSubunits === 'number' ? minBidAmountInSubunits : 1;
  const minBidMessage =
    minBidAmount > 1
      ? intl.formatMessage(
          { id: 'BidModal.bidAmountBelowHighest' },
          { minBid: formatMoney(intl, new Money(minBidAmount, marketplaceCurrency)) }
        )
      : intl.formatMessage({ id: 'BidModal.bidAmountTooLow' });
  const minBid = validators.moneySubUnitAmountAtLeast(minBidMessage, minBidAmount);
  const bidValidators = validators.composeValidators(priceRequired, minBid);

  return (
    <Modal
      id="BidModal"
      isOpen={isOpen}
      onClose={onClose}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling}
      contentClassName={css.modalContent}
    >
      <div className={css.header}>
        <h2 className={css.title}>
          <FormattedMessage id="BidModal.title" />
        </h2>
        <p className={css.subtitle}>
          <FormattedMessage id="BidModal.subtitle" values={{ listingTitle }} />
        </p>
      </div>

      <FinalForm
        onSubmit={values => {
          const bidAmountInSubunits = values.bidAmount?.amount;
          const currency = values.bidAmount?.currency || marketplaceCurrency;
          onSubmitBid({ bidAmountInSubunits, currency, message: values.message || '' });
        }}
        render={({ handleSubmit, invalid, pristine }) => (
          <Form onSubmit={handleSubmit} className={css.form}>
            <FieldCurrencyInput
              id="bidAmount"
              name="bidAmount"
              className={css.field}
              label={intl.formatMessage({ id: 'BidModal.bidAmountLabel' })}
              placeholder={intl.formatMessage({ id: 'BidModal.bidAmountPlaceholder' })}
              currencyConfig={appSettings.getCurrencyFormatting(marketplaceCurrency)}
              validate={bidValidators}
            />

            <FieldTextInput
              id="bidMessage"
              name="message"
              type="textarea"
              className={css.field}
              label={intl.formatMessage({ id: 'BidModal.messageLabel' })}
              placeholder={intl.formatMessage({ id: 'BidModal.messagePlaceholder' })}
            />

            {sendBidError ? (
              <p className={css.error}>
                {sendBidError?.message ? sendBidError.message : <FormattedMessage id="BidModal.sendBidError" />}
              </p>
            ) : null}

            <div className={css.actions}>
              <Button
                type="button"
                onClick={onClose}
                className={css.cancelButton}
              >
                <FormattedMessage id="BidModal.cancel" />
              </Button>
              <Button
                type="submit"
                inProgress={sendBidInProgress}
                disabled={invalid || sendBidInProgress}
                className={css.submitButton}
              >
                <FormattedMessage id="BidModal.submit" />
              </Button>
            </div>
          </Form>
        )}
      />
    </Modal>
  );
};

export default BidModal;
